import { useQuery } from "@tanstack/react-query";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * The set of roles we recognise on the client. Mirrors the server's
 * SetRoleBody enum in artifacts/api-server/src/routes/user-data.ts. Used
 * to validate the `role` field returned from /api/user/profile so we never
 * redirect to a path the router doesn't have.
 */
const KNOWN_ROLES = ["nurse", "patient", "doctor", "pharmacist", "physio", "ot", "coordinator"] as const;
export type KnownRole = (typeof KNOWN_ROLES)[number];

function isKnownRole(v: unknown): v is KnownRole {
  return typeof v === "string" && (KNOWN_ROLES as readonly string[]).includes(v);
}

export interface ApprovalCheckResult {
  /** null = no profile yet (brand-new user who hasn't chosen a role) */
  approvalStatus: "approved" | "pending" | "rejected" | null;
  hasProfile: boolean;
  isAdmin: boolean;
  /**
   * Server-side role from user_profiles.role. Null when the profile
   * doesn't exist yet or 403'd (pending/rejected non-admin users) — we
   * only get a confirmed role on the 200 path. The 403 body does not
   * include the role today, and we don't want to widen the API just for
   * that case.
   */
  role: KnownRole | null;
  onboardingComplete: boolean;
}

export const APPROVAL_QUERY_KEY = ["user", "profile", "approval"] as const;

async function fetchApprovalStatus(): Promise<ApprovalCheckResult> {
  const res = await fetch(`${basePath}/api/user/profile`, { credentials: "include" });
  if (res.status === 404) {
    return { approvalStatus: null, hasProfile: false, isAdmin: false, role: null, onboardingComplete: false };
  }
  if (res.status === 403) {
    const data = (await res.json().catch(() => ({}))) as { approvalStatus?: string };
    const s = data.approvalStatus;
    const approvalStatus: "pending" | "rejected" = s === "rejected" ? "rejected" : "pending";
    return { approvalStatus, hasProfile: true, isAdmin: false, role: null, onboardingComplete: false };
  }
  if (!res.ok) throw new Error(`profile-fetch-error:${res.status}`);
  const data = (await res.json()) as { approvalStatus?: string; isAdmin?: boolean; role?: string; hasProfile?: boolean; onboardingComplete?: boolean };
  // Respect the API hasProfile flag — new users with no profile get hasProfile: false.
  if (data.hasProfile === false) {
    return { approvalStatus: null, hasProfile: false, isAdmin: false, role: null, onboardingComplete: false };
  }
  const s = data.approvalStatus;
  const approvalStatus =
    s === "approved" || s === "pending" || s === "rejected" ? s : null;
  return {
    approvalStatus,
    hasProfile: true,
    isAdmin: Boolean(data.isAdmin),
    role: isKnownRole(data.role) ? data.role : null,
    onboardingComplete: data.onboardingComplete ?? false,
  };
}

export function useApprovalStatus(enabled: boolean) {
  return useQuery<ApprovalCheckResult>({
    queryKey: APPROVAL_QUERY_KEY,
    queryFn: fetchApprovalStatus,
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}
